"use client";

import { useEffect, useState } from "react";
import type { GlyphKind } from "@/lib/chat/workingSteps";

/**
 * A 3×3 dot glyph that steps like a tiny LED panel, one pattern per kind of
 * work. Moving dots mean something is happening now; done and failed are
 * still shapes. With reduced motion it holds a still frame.
 * See docs/superpowers/specs/2026-10-19-working-steps-in-chat-design.md.
 */

const FAINT = 0.14;
const STEP_MS = 120;
const STILL: ReadonlySet<GlyphKind> = new Set(["done", "failed"]);

const reading = (f: number) => {
  const n = f % 13;
  const lit = Math.min(n, 9);
  return Array.from({ length: 9 }, (_, i) => (n >= 11 ? FAINT : i < lit ? 1 : FAINT));
};

/** The nine opacities, row by row, for a frame. */
export function glyphFrame(kind: GlyphKind, frame: number, progress = 0): number[] {
  switch (kind) {
    case "reading":
    case "writing":
      return reading(frame);
    case "drafting": {
      const phase = frame % 6;
      return Array.from({ length: 9 }, (_, i) => {
        const d = Math.floor(i / 3) + (i % 3);
        return d === phase ? 1 : d === phase - 1 ? 0.5 : FAINT;
      });
    }
    case "checking": {
      const row = [0, 1, 2, 1][frame % 4];
      return Array.from({ length: 9 }, (_, i) => (Math.floor(i / 3) === row ? 1 : 0.22));
    }
    case "retrying": {
      const ring = [0, 1, 2, 5, 8, 7, 6, 3];
      const head = frame % 8;
      const out = Array(9).fill(FAINT);
      out[ring[head]] = 1;
      out[ring[(head + 7) % 8]] = 0.55;
      out[ring[(head + 6) % 8]] = 0.3;
      return out;
    }
    case "testing": {
      const done = Math.min(9, Math.round(Math.min(1, Math.max(0, progress)) * 9));
      return Array.from({ length: 9 }, (_, i) => (i < done ? 1 : i === done ? (frame % 2 ? 1 : 0.35) : FAINT));
    }
    case "done": {
      const tick = new Set([2, 3, 5, 7]);
      return Array.from({ length: 9 }, (_, i) => (tick.has(i) ? 1 : 0.1));
    }
    case "failed": {
      const cross = new Set([0, 2, 4, 6, 8]);
      return Array.from({ length: 9 }, (_, i) => (cross.has(i) ? 1 : 0.1));
    }
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function DotGlyph({ kind, progress = 0, animate = true, className = "" }: {
  kind: GlyphKind;
  progress?: number;
  /** False holds a still frame, e.g. for a step that is not current. */
  animate?: boolean;
  /** Carries the tone, e.g. "bg-busy" via toneDot. */
  className?: string;
}) {
  const [frame, setFrame] = useState(6);
  const moving = animate && !STILL.has(kind);
  useEffect(() => {
    if (!moving || prefersReducedMotion()) return;
    const id = setInterval(() => setFrame((f) => f + 1), STEP_MS);
    return () => clearInterval(id);
  }, [moving]);
  const values = glyphFrame(kind, moving ? frame : 6, progress);
  return (
    <span aria-hidden="true" data-glyph={kind} className="inline-grid shrink-0 grid-cols-3 gap-[2px]">
      {values.map((opacity, i) => (
        <span key={i} className={`size-[3.5px] rounded-full transition-opacity duration-75 ${className}`} style={{ opacity }} />
      ))}
    </span>
  );
}
